import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { CampaignResponse } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { communicationsApi } from '@/services/communications.api';
import { Alert, EmptyState, Spinner, StatusBadge } from '@/components/ui';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';

/** DRAFT and SENDING read as neutral/in-progress; SENT reads as done; FAILED
 *  as an error — the same three-state vocabulary `StatusBadge`'s `data-status`
 *  already carries for archived/active records elsewhere in the app. */
function badgeStatus(status: CampaignResponse['status']): string | undefined {
  if (status === 'SENT') return 'ACTIVE';
  if (status === 'FAILED') return 'INACTIVE';
  return undefined;
}

export function CampaignsListPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();

  const includeArchived = getShowArchivedRecordsPreference();
  const query = useQuery({
    queryKey: ['communications', 'campaigns', includeArchived],
    queryFn: ({ signal }) => communicationsApi.list({ limit: 50, includeArchived }, signal),
  });

  const campaigns = query.data?.data ?? [];

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>{t('communications.campaign.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('communications.campaign.count', { count: campaigns.length })}
            </p>
          )}
        </div>

        {can('communications.campaign.create') && (
          <Link to="/communications/new" className="btn" data-variant="primary">
            {t('communications.campaign.new')}
          </Link>
        )}
      </header>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && campaigns.length === 0 && (
        <EmptyState
          title={t('common.states.empty')}
          hint={
            can('communications.campaign.create') ? t('communications.campaign.new') : undefined
          }
        />
      )}

      {campaigns.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('communications.campaign.subject')}</th>
                <th scope="col">{t('communications.campaign.channel')}</th>
                <th scope="col">{t('common.labels.status')}</th>
                <th scope="col">{t('communications.campaign.reach')}</th>
                <th scope="col">{t('communications.campaign.date')}</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td>
                    <Link to={`/communications/${campaign.id}`}>
                      {campaign.subject ??
                        t(`communications.campaign.channelLabel.${campaign.channel}`)}
                    </Link>
                  </td>
                  <td>{t(`communications.campaign.channelLabel.${campaign.channel}`)}</td>
                  <td>
                    <StatusBadge
                      status={badgeStatus(campaign.status) ?? campaign.status}
                      label={t(`communications.campaign.status.${campaign.status}`)}
                    />
                  </td>
                  <td>
                    {campaign.status === 'DRAFT'
                      ? '—'
                      : t('communications.campaign.reachCount', {
                          values: { sent: campaign.sentCount, total: campaign.recipientCount },
                        })}
                  </td>
                  <td>{formatDateTime(campaign.sentAt ?? campaign.createdAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
